import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { storableError } from '../util/errors';

/**
 * Which providers has the current user already contacted?
 *
 * Client feedback item 12: a provider is shown as an acronym until the viewer
 * engages, then the full company name is revealed. This marketplace takes no
 * payments, so the client's "after payment" is the inquiry — in the
 * default-inquiry process, `transition/inquire-without-payment` creates a real
 * transaction, which is the durable record of "this member contacted that
 * provider".
 *
 * Deliberately a set of listing ids rather than transactions: the only question
 * any caller asks is "may this viewer see this provider's name", and a member
 * who inquired twice should not be a different case from one who inquired once.
 *
 * Note this is a DISPLAY gate. It is safe as one only because access control is
 * enforced a level up by Sharetribe's private-marketplace setting
 * (`marketplace.private` + `users.requireApprovalToJoin`), which stops anyone
 * unapproved reading the API at all. See docs/item-12-provider-name-gating.md —
 * without that setting on, the names remain readable through the public API and
 * this gate is cosmetic.
 */

// One page is plenty: this is "have I ever contacted them", not a transaction
// list. A member past 100 inquiries would lose the reveal on the oldest ones,
// which degrades to showing an acronym — never to leaking a name.
const ORDERS_PAGE_SIZE = 100;

const initialState = {
  listingIds: [],
  fetched: false,
  inProgress: false,
  error: null,
};

const fetchContactedProvidersPayloadCreator = (_, { extra: sdk, rejectWithValue }) =>
  sdk.transactions
    .query({ only: 'order', page: 1, perPage: ORDERS_PAGE_SIZE, include: ['listing'] })
    .then(response => {
      const listingIds = response.data.data
        .map(tx => tx.relationships?.listing?.data?.id?.uuid)
        .filter(Boolean);
      // De-duplicated: several inquiries to one provider are one relationship.
      return [...new Set(listingIds)];
    })
    .catch(e => rejectWithValue(storableError(e)));

export const fetchContactedProvidersThunk = createAsyncThunk(
  'contactedProviders/fetch',
  fetchContactedProvidersPayloadCreator,
  {
    // Guards against the duplicate dispatches that would otherwise happen when
    // a page renders many ProviderCards, each asking the same question.
    condition: (_, { getState }) => {
      const { fetched, inProgress } = getState().contactedProviders;
      return !fetched && !inProgress;
    },
  }
);

const contactedProvidersSlice = createSlice({
  name: 'contactedProviders',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchContactedProvidersThunk.pending, state => {
        state.inProgress = true;
        state.error = null;
      })
      .addCase(fetchContactedProvidersThunk.fulfilled, (state, action) => {
        state.inProgress = false;
        state.fetched = true;
        state.listingIds = action.payload;
      })
      .addCase(fetchContactedProvidersThunk.rejected, (state, action) => {
        state.inProgress = false;
        // `fetched` stays false so a later navigation retries. A failed lookup
        // must leave the viewer LOCKED, never optimistically unlocked.
        state.error = action.payload;
      });
  },
});

/** @returns {Array<string>} listing ids the current user has an order for */
export const selectContactedListingIds = state => state.contactedProviders.listingIds;

export default contactedProvidersSlice.reducer;
